/**
 * Building the organization list for the portal-profile picker.
 *
 * <h3>Why this needs a function at all</h3>
 * `GET /organizations/businesses` does NOT return only businesses. It returns
 * every accessible organization carrying a non-blank `businessId` — which
 * includes service providers, because CatchMyCall, XtremeGenAI and Teeoff all
 * have one. Concatenating it with `GET /organizations/services` therefore listed
 * every provider twice.
 *
 * (Green Giraffe was the exception and appeared once, because its `businessId`
 * is NULL and the businesses endpoint filters those out — which is also why both
 * calls are still needed: dropping the providers call would lose it entirely.)
 */

export interface PickerOrg {
  id: string;
  name: string;
  type?: string;
  parentOrganizationId?: string | null;
}

export const isProviderOrg = (o: PickerOrg | null | undefined): boolean =>
  (o?.type || '').toUpperCase() === 'SERVICE';

/**
 * Merge the two listings into one, each organization exactly once.
 *
 * <p>The providers list is seeded FIRST and wins on conflict. It is the
 * authoritative statement that an organization is a SERVICE org, and the picker
 * uses that to decide whether to offer brand fields — taking the other row's
 * word for it would mean offering a provider only a tab list.
 *
 * <p>Sorted providers-first then alphabetically, which is the order somebody
 * configuring a reseller actually works in: pick the provider, brand it, then
 * work down its businesses.
 */
export function mergeOrganizations(
  providers: PickerOrg[],
  businesses: PickerOrg[],
): PickerOrg[] {
  const byId = new Map<string, PickerOrg>();

  for (const p of providers) {
    if (p?.id) byId.set(p.id, { ...p, type: p.type ?? 'SERVICE' });
  }
  for (const b of businesses) {
    if (b?.id && !byId.has(b.id)) byId.set(b.id, b);
  }

  // Array.from, not a spread: this repo's tsconfig target predates iterator
  // spreading, and `[...map.values()]` needs downlevelIteration.
  return Array.from(byId.values()).sort((a, b) => {
    const ap = isProviderOrg(a) ? 0 : 1;
    const bp = isProviderOrg(b) ? 0 : 1;
    return ap !== bp ? ap - bp : (a.name || '').localeCompare(b.name || '');
  });
}

/** A provider and the businesses beneath it, as the picker renders them. */
export interface OrgGroup {
  /** Group heading — a provider's name, or "Without a service provider". */
  label: string;
  options: PickerOrg[];
}

export const UNPARENTED_LABEL = 'Without a service provider';

/**
 * Group the merged list by service provider.
 *
 * <p>A flat list is unusable here: a platform admin can reach every
 * organization, so the picker holds dozens of names with nothing to say which
 * reseller each belongs to. The task is always "configure this reseller and its
 * customers", so the hierarchy the profile itself uses — brand from the
 * provider, tabs per business — is the order the list should be read in.
 *
 * <p>A business whose provider is not in the list is NOT dropped. It is grouped
 * under {@link UNPARENTED_LABEL}, because a business with no reachable parent is
 * exactly the case that cannot inherit a brand and most needs to be visible —
 * hiding it would make a misconfigured organization invisible in the one screen
 * that could fix it.
 */
export function groupByProvider(orgs: PickerOrg[]): OrgGroup[] {
  const providers = orgs.filter(isProviderOrg);
  const providerIds = new Set(providers.map((p) => p.id));

  const groups: OrgGroup[] = providers.map((p) => ({
    label: p.name,
    // The provider heads its own group: it is selectable, and it is the row
    // that carries the brand.
    options: [
      p,
      ...orgs
        .filter((o) => !isProviderOrg(o) && o.parentOrganizationId === p.id)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    ],
  }));

  const orphans = orgs
    .filter((o) => !isProviderOrg(o)
      && (!o.parentOrganizationId || !providerIds.has(o.parentOrganizationId)))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (orphans.length > 0) groups.push({ label: UNPARENTED_LABEL, options: orphans });
  return groups;
}
